/**
 * Bid-checker product-analytics events.
 *
 * Thin typed wrappers over the shared `sendEvent`. Every helper sets
 * `object_type`/`object_id`; `app_id`, `lane`, `app_version` and `is_internal`
 * are auto-stamped by the shared telemetry module — never pass them.
 * See `shared/telemetry/EVENT_CATALOG.md` in darwin-gov/launchpad.
 *
 * PII rule: props carry ids / types / counts / enums only — never vendor
 * names, project titles, email addresses, filenames, or document content.
 */
import { sendEvent } from '@darwin-launchpad/legacy-shared/telemetry';

export function trackRfpProjectCreated(
  projectId: string,
  args?: { cover_letter_only?: boolean },
): void {
  sendEvent('rfp_project_created', {
    object_type: 'project',
    object_id: projectId,
    ...args,
  });
}

export function trackRfpProjectClosed(projectId: string): void {
  sendEvent('rfp_project_closed', {
    object_type: 'project',
    object_id: projectId,
  });
}

export function trackVendorResponsesUploaded(
  projectId: string,
  args: { total_vendors: number; total_files: number },
): void {
  sendEvent('vendor_responses_uploaded', {
    object_type: 'vendor_response',
    object_id: projectId,
    ...args,
  });
}

export function trackVendorResponseViewed(
  responseId: string,
  args?: { status?: string },
): void {
  sendEvent('vendor_response_viewed', {
    object_type: 'vendor_response',
    object_id: responseId,
    ...args,
  });
}

export function trackUnifiedDocumentDownloaded(responseId: string): void {
  sendEvent('unified_document_downloaded', {
    object_type: 'vendor_response',
    object_id: responseId,
  });
}

export function trackVendorResponseReevaluated(
  projectId: string,
  args: { source: 'single' | 'bulk'; response_count: number },
): void {
  sendEvent('vendor_response_reevaluated', {
    object_type: 'vendor_response',
    object_id: projectId,
    ...args,
  });
}

export function trackVendorResponseStatusChanged(
  responseId: string,
  args: { status: string },
): void {
  sendEvent('vendor_response_status_changed', {
    object_type: 'vendor_response',
    object_id: responseId,
    ...args,
  });
}

export function trackVendorResponsesExportedExcel(
  projectId: string,
  args?: { response_count?: number },
): void {
  sendEvent('vendor_responses_exported_excel', {
    object_type: 'project',
    object_id: projectId,
    ...args,
  });
}

export function trackEvaluationEvidenceViewed(
  evaluationId: string,
  args?: { source_page?: number },
): void {
  sendEvent('evaluation_evidence_viewed', {
    object_type: 'evaluation',
    object_id: evaluationId,
    ...args,
  });
}
